import { create } from "zustand";

export interface PersistedSettings {
  statusRefreshMs: number;
  activityRefreshMs: number;
  notificationsAutoDismiss: boolean;
  notificationsDurationMs: number;
  searchDefaultRows: number;
}

interface SettingsState extends PersistedSettings {
  setStatusRefreshMs: (value: number) => void;
  setActivityRefreshMs: (value: number) => void;
  setNotificationsAutoDismiss: (enabled: boolean) => void;
  setNotificationsDurationMs: (value: number) => void;
  setSearchDefaultRows: (value: number) => void;
  resetDefaults: () => void;
}

const STORAGE_KEY = "pec-settings";

const defaultSettings: PersistedSettings = {
  statusRefreshMs: 15_000,
  activityRefreshMs: 20_000,
  notificationsAutoDismiss: true,
  notificationsDurationMs: 6_000,
  searchDefaultRows: 25
};

function loadSettings(): Partial<PersistedSettings> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Partial<PersistedSettings>;
    const sanitized: Partial<PersistedSettings> = {};

    if (typeof parsed.statusRefreshMs === "number" && parsed.statusRefreshMs >= 5_000) {
      sanitized.statusRefreshMs = parsed.statusRefreshMs;
    }
    if (typeof parsed.activityRefreshMs === "number" && parsed.activityRefreshMs >= 5_000) {
      sanitized.activityRefreshMs = parsed.activityRefreshMs;
    }
    if (typeof parsed.notificationsAutoDismiss === "boolean") {
      sanitized.notificationsAutoDismiss = parsed.notificationsAutoDismiss;
    }
    if (typeof parsed.notificationsDurationMs === "number" && parsed.notificationsDurationMs >= 2_000) {
      sanitized.notificationsDurationMs = parsed.notificationsDurationMs;
    }
    if (typeof parsed.searchDefaultRows === "number" && [10, 25, 50, 100].includes(parsed.searchDefaultRows)) {
      sanitized.searchDefaultRows = parsed.searchDefaultRows;
    }

    return sanitized;
  } catch (error) {
    console.warn("Failed to load settings", error);
    return {};
  }
}

function persistSettings(settings: PersistedSettings) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("Failed to persist settings", error);
  }
}

function pickPersisted(state: SettingsState): PersistedSettings {
  return {
    statusRefreshMs: state.statusRefreshMs,
    activityRefreshMs: state.activityRefreshMs,
    notificationsAutoDismiss: state.notificationsAutoDismiss,
    notificationsDurationMs: state.notificationsDurationMs,
    searchDefaultRows: state.searchDefaultRows
  };
}

export const useSettingsStore = create<SettingsState>((set) => {
  const initial = { ...defaultSettings, ...loadSettings() };

  const update = (partial: Partial<PersistedSettings>) => {
    set((state) => {
      const current = pickPersisted(state);
      const next = { ...current, ...partial };
      persistSettings(next);
      return partial as Partial<SettingsState>;
    });
  };

  return {
    ...initial,
    setStatusRefreshMs: (value) => update({ statusRefreshMs: value }),
    setActivityRefreshMs: (value) => update({ activityRefreshMs: value }),
    setNotificationsAutoDismiss: (enabled) => update({ notificationsAutoDismiss: enabled }),
    setNotificationsDurationMs: (value) => update({ notificationsDurationMs: value }),
    setSearchDefaultRows: (value) => update({ searchDefaultRows: value }),
    resetDefaults: () => {
      persistSettings(defaultSettings);
      set(() => ({ ...defaultSettings }));
    }
  };
});
